import moment from 'moment';

const normalizeRange = (range) => ({
  start: moment(range.start),
  end: moment(range.end),
});

const getFilterRange = (store) => store.getters['filter/date'];

const resolveInterval = (store, { interval, allowMinute = true, forceInterval } = {}) => {
  let resolved = interval ?? store.getters['filter/interval'];

  if (forceInterval) {
    resolved = forceInterval;
  } else if (!allowMinute && resolved === 'minute') {
    resolved = 'hour';
  }

  if (resolved !== store.getters['filter/interval']) {
    store.commit('filter/SET_INTERVAL', resolved);
  }

  return resolved;
};

export const initAssetSummary = async (store, options = {}) => {
  console.log('[initAssetSummary] === START ===');
  const {
    dateRange,
    setFilterDate = false,
    interval,
    allowMinute = true,
    forceInterval,
    withDevices = false,
    withLastData = false,
  } = options;

  console.log('[initAssetSummary] Options:', {
    dateRange, withDevices, withLastData, interval,
  });

  let assets = store.getters['asset/assets'] || [];
  let loadedAssets = false;

  console.log('[initAssetSummary] Initial assets count:', assets.length);

  // Always load assets first if empty or if we need to ensure fresh data
  if (assets.length === 0) {
    console.log('[initAssetSummary] No assets, fetching from server...');
    await store.dispatch('asset/getAssetsData');
    assets = store.getters['asset/assets'] || [];
    loadedAssets = true;
    console.log('[initAssetSummary] Assets loaded, count:', assets.length);
  }

  if (withDevices) {
    const needsDevices = assets.some((asset) => !Array.isArray(asset.devices));
    console.log('[initAssetSummary] Needs devices?', needsDevices);
    if (loadedAssets || needsDevices) {
      console.log('[initAssetSummary] Fetching devices...');
      await store.dispatch('asset/getAssetsDevices');
      assets = store.getters['asset/assets'] || [];
      console.log('[initAssetSummary] After getAssetsDevices, assets count:', assets.length);
    }
  }

  if (withLastData && loadedAssets) {
    console.log('[initAssetSummary] Fetching last data...');
    await store.dispatch('asset/getLastData');
    assets = store.getters['asset/assets'] || [];
  }

  let currentMassId = store.getters['filter/currentMassId'];
  console.log('[initAssetSummary] currentMassId before check:', currentMassId);

  if (!currentMassId && assets.length > 0) {
    console.log('[initAssetSummary] No currentMassId, setting to first asset:', assets[0].massId);
    await store.dispatch('filter/setCurrentMassId', assets[0].massId);
    currentMassId = store.getters['filter/currentMassId'];
    console.log('[initAssetSummary] currentMassId after dispatch:', currentMassId);
  }

  const resolvedInterval = resolveInterval(store, { interval, allowMinute, forceInterval });
  const resolvedRange = dateRange ? normalizeRange(dateRange) : getFilterRange(store);

  console.log('[initAssetSummary] Resolved interval:', resolvedInterval);
  console.log('[initAssetSummary] Resolved range:', resolvedRange.start.format(), resolvedRange.end.format());

  if (setFilterDate && dateRange) {
    store.commit('filter/SET_DATE', resolvedRange);
  }

  if (currentMassId) {
    // Verify asset exists before fetching summary
    // Convert to plain array to avoid Vue Observable issues
    const assetsArray = Array.isArray(assets) ? assets : Object.values(assets);
    const currentMassIdStr = String(currentMassId);
    const assetExists = assetsArray.some((asset) => {
      const match = String(asset.massId) === currentMassIdStr;
      return match;
    });

    console.log('[initAssetSummary] Asset exists?', assetExists);

    if (!assetExists) {
      console.error('[initAssetSummary] Asset not found!');
      return {
        assets: store.getters['asset/assets'] || [],
        currentMassId,
        interval: resolvedInterval,
        dateRange: resolvedRange,
      };
    }

    // Align start/end to the resolved interval boundaries to match aggregation buckets
    const fetchStart = moment(resolvedRange.start).startOf(resolvedInterval);
    const fetchEnd = moment(resolvedRange.end).endOf(resolvedInterval);

    console.log('[initAssetSummary] Calling fetchSummary with:', {
      start: fetchStart.format(),
      end: fetchEnd.format(),
      interval: resolvedInterval,
      massId: currentMassId,
    });

    await store.dispatch('asset/fetchSummary', {
      start: fetchStart,
      end: fetchEnd,
      interval: resolvedInterval,
      massId: currentMassId,
    });

    console.log('[initAssetSummary] fetchSummary completed');
  } else {
    console.warn('[initAssetSummary] No massId, skipping fetchSummary');
  }

  console.log('[initAssetSummary] === END ===');
  return {
    assets: store.getters['asset/assets'] || [],
    currentMassId,
    interval: resolvedInterval,
    dateRange: resolvedRange,
  };
};

export const applyAssetSummaryFilter = async (store, options = {}) => {
  const {
    dateRange,
    interval,
    massId,
    setFilterDate = false,
  } = options;

  if (massId) {
    await store.dispatch('filter/setCurrentMassId', massId);
  }

  if (interval) {
    await store.dispatch('filter/setInterval', interval);
  }

  const resolvedMassId = massId || store.getters['filter/currentMassId'];
  const resolvedInterval = interval || store.getters['filter/interval'];
  const resolvedRange = dateRange ? normalizeRange(dateRange) : getFilterRange(store);

  if (setFilterDate && dateRange) {
    store.commit('filter/SET_DATE', resolvedRange);
  }

  if (resolvedMassId) {
    // Ensure assets are loaded before fetching summary
    let assets = store.getters['asset/assets'] || [];

    if (assets.length === 0) {
      await store.dispatch('asset/getAssetsData');
      assets = store.getters['asset/assets'] || [];
    }

    // Verify asset exists (with string comparison)
    const assetsArray = Array.isArray(assets) ? assets : Object.values(assets);
    const resolvedMassIdStr = String(resolvedMassId);

    // eslint-disable-next-line no-unused-vars
    let foundAsset = null;
    const assetExists = assetsArray.some((asset) => {
      const assetMassIdStr = String(asset.massId);
      const match = assetMassIdStr === resolvedMassIdStr;
      if (match) foundAsset = asset;
      return match;
    });

    if (!assetExists) {
      console.error(`[APPLY FILTER] Asset with massId ${resolvedMassId} not found!`);
      return {
        massId: resolvedMassId,
        interval: resolvedInterval,
        dateRange: resolvedRange,
      };
    }
    // Align start/end to the resolved interval boundaries so server aggregation
    // doesn't return buckets that fall outside the user's selected range.
    const fetchStart = moment(resolvedRange.start).startOf(resolvedInterval);
    const fetchEnd = moment(resolvedRange.end).endOf(resolvedInterval);
    await store.dispatch('asset/fetchSummary', {
      start: fetchStart,
      end: fetchEnd,
      interval: resolvedInterval,
      massId: resolvedMassId,
    });
  }

  return {
    massId: resolvedMassId,
    interval: resolvedInterval,
    dateRange: resolvedRange,
  };
};
